/**
 * Portadas puestas a mano.
 *
 * Suwayomi no sabe de esto: sus mutaciones de metadatos solo guardan texto,
 * asi que la imagen vive en el propio telefono, en IndexedDB. La contrapartida
 * es que no viaja a otros dispositivos, y que si iOS borra los datos del sitio
 * se va con ellos (de ahi que convenga tener el almacenamiento protegido).
 */

import { almacen } from './almacen'

const PREFIJO = 'portada:'
const LADO_MAX = 700

function clave(mangaId: number): string {
  return `${PREFIJO}${mangaId}`
}

/**
 * Encoge la imagen antes de guardarla.
 *
 * Una foto del carrete son 12 megapixeles y varios MB, y aqui se ve a 108 px
 * de ancho en la rejilla. Guardarla entera seria gastar el espacio del movil
 * —el mismo que usan los capitulos descargados— para nada.
 *
 * Se dibuja con <img> y no con `createImageBitmap` a proposito: asi Safari se
 * encarga de descodificar, que es lo unico que abre los HEIC del iPhone y lo
 * unico que no se atraganta con imagenes muy grandes.
 */
async function encoger(archivo: Blob): Promise<Blob> {
  const url = URL.createObjectURL(archivo)
  try {
    const imagen = new Image()
    await new Promise<void>((resolver, rechazar) => {
      imagen.onload = () => resolver()
      imagen.onerror = () => rechazar(new Error('No se pudo leer la imagen'))
      imagen.src = url
    })

    const factor = Math.min(1, LADO_MAX / Math.max(imagen.naturalWidth, imagen.naturalHeight))
    const lienzo = document.createElement('canvas')
    lienzo.width = Math.max(1, Math.round(imagen.naturalWidth * factor))
    lienzo.height = Math.max(1, Math.round(imagen.naturalHeight * factor))

    const pincel = lienzo.getContext('2d')
    if (!pincel) throw new Error('No se pudo preparar la imagen')
    pincel.drawImage(imagen, 0, 0, lienzo.width, lienzo.height)

    const resultado = await new Promise<Blob | null>((resolver) =>
      lienzo.toBlob(resolver, 'image/jpeg', 0.85),
    )
    if (!resultado) throw new Error('No se pudo convertir la imagen')
    return resultado
  } finally {
    URL.revokeObjectURL(url)
  }
}

export async function guardarPortada(mangaId: number, archivo: Blob): Promise<void> {
  await almacen.guardar(clave(mangaId), await encoger(archivo))
}

export async function leerPortada(mangaId: number): Promise<Blob | null> {
  return almacen.leer(clave(mangaId))
}

export async function borrarPortada(mangaId: number): Promise<void> {
  await almacen.borrar(clave(mangaId))
}
